import { Home, Banknote, Receipt, Wallet } from "lucide-react";
import { fmtEUR } from "../../lib/calculations";
import type { ComputedResults, SimulationInput } from "../../lib/types";

interface Props {
  input: SimulationInput;
  results: ComputedResults;
  step: number;
  variant?: "sidebar" | "embedded";
}

const TYPE_LABEL: Record<SimulationInput["typeLocation"], string> = {
  longue: "Longue durée",
  moyenne: "Moyenne durée",
  courte: "Courte durée",
};

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular font-semibold text-foreground text-right">{value}</span>
    </div>
  );
}

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Home;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        <span>{title}</span>
      </div>
      <div className="space-y-1.5 border-l-2 border-border pl-3">{children}</div>
    </div>
  );
}

function SummaryContent({ input, results, step }: Omit<Props, "variant">) {
  const sections: React.ReactNode[] = [];

  if (step >= 1) {
    sections.push(
      <Section key="bien" icon={Home} title="Le bien">
        {input.prixAcquisition > 0 && <Row label="Prix" value={fmtEUR(input.prixAcquisition)} />}
        {input.travaux > 0 && <Row label="Travaux" value={fmtEUR(input.travaux)} />}
        {input.mobilier > 0 && <Row label="Mobilier" value={fmtEUR(input.mobilier)} />}
        <Row label="Région" value={input.region} />
        <Row label="Total" value={fmtEUR(results.investissementTotal)} />
      </Section>
    );
  }

  if (step >= 2 && input.loyerMensuel > 0) {
    sections.push(
      <Section key="revenus" icon={Banknote} title="Les revenus">
        <Row label="Loyer / mois" value={fmtEUR(input.loyerMensuel)} />
        <Row label="Type" value={TYPE_LABEL[input.typeLocation]} />
        <Row label="Vacance" value={`${input.tauxVacance}%`} />
        <Row label="Revenus / an" value={fmtEUR(results.revenusBrutsAnnuels)} />
      </Section>
    );
  }

  if (step >= 3) {
    sections.push(
      <Section key="charges" icon={Receipt} title="Les charges">
        <Row label="Mode" value={input.chargesMode === "simple" ? "Simple" : "Détaillé"} />
        <Row label="Total / an" value={fmtEUR(results.chargesTotales)} />
      </Section>
    );
  }

  if (step >= 4) {
    sections.push(
      <Section key="financement" icon={Wallet} title="Financement">
        <Row label="Apport" value={fmtEUR(input.apport)} />
        <Row label="Crédit" value={input.hasCredit ? "Oui" : "Cash 100%"} />
        {input.hasCredit && (
          <>
            <Row label="Emprunt" value={fmtEUR(input.montantEmprunte)} />
            <Row label="Mensualité" value={fmtEUR(results.mensualiteCredit)} />
          </>
        )}
      </Section>
    );
  }

  return (
    <>
      {sections.map((s, i) => (
        <div key={i} className="space-y-5">
          {i > 0 && <div className="border-t border-border" />}
          {s}
        </div>
      ))}
    </>
  );
}

export function SummaryPanel({ input, results, step, variant = "sidebar" }: Props) {
  if (variant === "embedded") {
    return (
      <div className="space-y-5">
        <SummaryContent input={input} results={results} step={step} />
      </div>
    );
  }
  return (
    <aside
      className="sticky top-6 hidden w-[280px] shrink-0 rounded-xl bg-secondary/60 p-6 lg:block"
      aria-label="Récapitulatif de votre simulation"
    >
      <h3 className="mb-4 text-base font-semibold text-foreground">Votre simulation</h3>
      <div className="space-y-5">
        <SummaryContent input={input} results={results} step={step} />
      </div>
    </aside>
  );
}
